import type { eLanguage } from '@shared/constants';
import { useCallback, useMemo } from 'react';

import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import Dropdown from '@/components/atoms/Dropdown';
import { useAuth } from '@/context/auth';
import { useLocalization } from '@/context/localization';

import type { ILanguageSelectorProps } from './model';

export const LanguageSelector = ({ onChange, availableLocales }: ILanguageSelectorProps) => {
  const {
    state: { locale },
    actions: { setLocale },
  } = useLocalization();
  const { user } = useAuth();

  const { useUpdateUser } = useUserQueryActions();
  const { mutate: updateUser } = useUpdateUser();

  const getFlagIconSrc = useCallback((code: eLanguage) => {
    return `/assets/images/flags/${code.slice(-2)}.svg`;
  }, []);

  const mappedAvailableLocales = useMemo(
    () =>
      availableLocales.map((item) => (
        <li key={item.code}>
          <button
            type='button'
            onClick={() => {
              setLocale(item.code);
              onChange(item.code);
              if (user) {
                updateUser({ userId: user.id, userData: { ...user, cultureName: item.code } });
              }
            }}
            className={`hover:!bg-c16 flex w-full rounded-lg hover:!text-white dark:text-white ${
              locale === item.code ? 'dark:bg-c16' : ''
            }`}
          >
            <img
              src={getFlagIconSrc(item?.code)}
              alt='flag'
              className='h-5 w-5 rounded-full object-cover'
            />
            <span className='ml-[.3em] ltr:ml-3 rtl:mr-3'>{item.name}</span>
          </button>
        </li>
      )),
    [availableLocales, getFlagIconSrc, locale, onChange, setLocale, updateUser, user],
  );

  const isSingleLanguage = (availableLocales?.length ?? 0) === 1;

  return (
    <div className='dropdown shrink-0'>
      <Dropdown
        offset={[0, 8]}
        placement='bottom-end'
        poperClass='z-[10000]'
        btnClassName='flex h-10 w-10 items-center justify-center rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60'
        button={
          <img
            className='h-6 w-6 rounded-full object-cover'
            src={getFlagIconSrc(locale)}
            alt='flag'
          />
        }
      >
        <ul
          className={`text-dark dark:!bg-c4 dark:text-white-dark dark:text-white-light/90 grid w-auto gap-2 !px-2 font-semibold ${
            isSingleLanguage ? 'grid-cols-1' : 'grid-cols-2'
          }`}
        >
          {mappedAvailableLocales}
        </ul>
      </Dropdown>
    </div>
  );
};
